import type { SegmentType } from '../types';

/**
 * Compute the visual midpoint of a segment for label placement.
 */
export function getSegmentMidpoint(
  from: { x: number; y: number },
  to: { x: number; y: number },
  fromDir: { dx: number; dy: number },
  toDir: { dx: number; dy: number },
  type: SegmentType,
): { x: number; y: number } {
  switch (type) {
    case 'straight':
      return { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };

    case 'bezier': {
      const dist = Math.hypot(to.x - from.x, to.y - from.y);
      const cpOffset = Math.max(50, dist * 0.4);
      const cp1 = { x: from.x + fromDir.dx * cpOffset, y: from.y + fromDir.dy * cpOffset };
      const cp2 = { x: to.x + toDir.dx * cpOffset, y: to.y + toDir.dy * cpOffset };
      // Cubic bezier at t=0.5: B(0.5) = (1-t)^3*P0 + 3*(1-t)^2*t*P1 + 3*(1-t)*t^2*P2 + t^3*P3
      const t = 0.5;
      const mt = 1 - t;
      const x = mt * mt * mt * from.x + 3 * mt * mt * t * cp1.x + 3 * mt * t * t * cp2.x + t * t * t * to.x;
      const y = mt * mt * mt * from.y + 3 * mt * mt * t * cp1.y + 3 * mt * t * t * cp2.y + t * t * t * to.y;
      return { x, y };
    }

    case 'ortho90': {
      const OFFSET = 10;
      const e1 = { x: from.x + fromDir.dx * OFFSET, y: from.y + fromDir.dy * OFFSET };
      const e2 = { x: to.x + toDir.dx * OFFSET, y: to.y + toDir.dy * OFFSET };
      const fromH = Math.abs(fromDir.dx) > Math.abs(fromDir.dy);
      const corner = fromH
        ? { x: e2.x, y: e1.y }
        : { x: e1.x, y: e2.y };
      const points = [from, e1, corner, e2, to];

      let totalLen = 0;
      for (let i = 1; i < points.length; i++) {
        totalLen += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
      }
      if (totalLen === 0) return { x: from.x, y: from.y };
      const halfLen = totalLen / 2;
      let walked = 0;
      for (let i = 1; i < points.length; i++) {
        const segLen = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
        if (walked + segLen >= halfLen) {
          const t = segLen > 0 ? (halfLen - walked) / segLen : 0;
          return {
            x: points[i - 1].x + (points[i].x - points[i - 1].x) * t,
            y: points[i - 1].y + (points[i].y - points[i - 1].y) * t,
          };
        }
        walked += segLen;
      }
      return { x: to.x, y: to.y };
    }

    case 'angle45': {
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const isHorizontalExit = Math.abs(fromDir.dx) > Math.abs(fromDir.dy);
      const diag = Math.min(absDx, absDy);
      const axisFirst = (absDx >= absDy) ? isHorizontalExit : !isHorizontalExit;
      let mid: { x: number; y: number };
      if (axisFirst) {
        mid = isHorizontalExit
          ? { x: from.x + Math.sign(dx) * (absDx - diag), y: from.y }
          : { x: from.x, y: from.y + Math.sign(dy) * (absDy - diag) };
      } else {
        mid = { x: from.x + Math.sign(dx) * diag, y: from.y + Math.sign(dy) * diag };
      }
      // Midpoint along the 2-segment path
      const len1 = Math.hypot(mid.x - from.x, mid.y - from.y);
      const len2 = Math.hypot(to.x - mid.x, to.y - mid.y);
      const totalLen = len1 + len2;
      if (totalLen === 0) return { x: from.x, y: from.y };
      const halfLen = totalLen / 2;
      if (halfLen <= len1) {
        const t = len1 > 0 ? halfLen / len1 : 0;
        return { x: from.x + (mid.x - from.x) * t, y: from.y + (mid.y - from.y) * t };
      } else {
        const t = len2 > 0 ? (halfLen - len1) / len2 : 0;
        return { x: mid.x + (to.x - mid.x) * t, y: mid.y + (to.y - mid.y) * t };
      }
    }

    case 'elbowed': {
      const OFFSET = 20;
      const e1 = { x: from.x + fromDir.dx * OFFSET, y: from.y + fromDir.dy * OFFSET };
      const e2 = { x: to.x + toDir.dx * OFFSET, y: to.y + toDir.dy * OFFSET };
      const fromH = Math.abs(fromDir.dx) > Math.abs(fromDir.dy);
      const toH = Math.abs(toDir.dx) > Math.abs(toDir.dy);

      let points: { x: number; y: number }[];
      if (fromH && toH) {
        const midX = (e1.x + e2.x) / 2;
        points = [from, e1, { x: midX, y: e1.y }, { x: midX, y: e2.y }, e2, to];
      } else if (!fromH && !toH) {
        const midY = (e1.y + e2.y) / 2;
        points = [from, e1, { x: e1.x, y: midY }, { x: e2.x, y: midY }, e2, to];
      } else if (fromH && !toH) {
        points = [from, e1, { x: e2.x, y: e1.y }, e2, to];
      } else {
        points = [from, e1, { x: e1.x, y: e2.y }, e2, to];
      }

      // Walk the polyline to find the midpoint at half total length
      let totalLen = 0;
      for (let i = 1; i < points.length; i++) {
        totalLen += Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
      }
      if (totalLen === 0) return { x: from.x, y: from.y };
      const halfLen = totalLen / 2;
      let walked = 0;
      for (let i = 1; i < points.length; i++) {
        const segLen = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
        if (walked + segLen >= halfLen) {
          const t = segLen > 0 ? (halfLen - walked) / segLen : 0;
          return {
            x: points[i - 1].x + (points[i].x - points[i - 1].x) * t,
            y: points[i - 1].y + (points[i].y - points[i - 1].y) * t,
          };
        }
        walked += segLen;
      }
      return { x: to.x, y: to.y };
    }
  }
}
